import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Alert,
  Stepper,
  Step,
  StepLabel,
  Divider,
  CircularProgress
} from '@mui/material';
import { api } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import EuroCashCounterDialog from './EuroCashCounterDialog';
import { BanknoteButtonArt } from './euro/EuroDenominationArt';

const formatEuro = (value) => `€ ${(parseFloat(value) || 0).toFixed(2)}`;

const formatDateTime = (value) => {
  if (!value) return '-';
  return new Date(value).toLocaleString('it-IT');
};

const parseAmount = (value) => parseFloat(String(value).replace(',', '.'));

const amountsDiffer = (actual, expected) => {
  if (expected == null || Number.isNaN(parseAmount(expected))) return false;
  return Math.abs(parseAmount(actual) - parseAmount(expected)) > 0.009;
};

function RecapRow({ label, value, highlight = false }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1 }}>
      <Typography variant="body1" color={highlight ? 'primary' : 'text.primary'} fontWeight={highlight ? 'bold' : 'normal'}>
        {label}
      </Typography>
      <Typography variant="body1" fontWeight={highlight ? 'bold' : 'medium'}>
        {value}
      </Typography>
    </Box>
  );
}

function getUserLabel(userRef) {
  return userRef?.name || userRef?.email || 'Operatore sconosciuto';
}

function PreviousOperationAlert({ operation, mode }) {
  if (!operation) {
    return (
      <Alert severity="info" sx={{ mb: 2 }}>
        Nessuna operazione precedente registrata.
      </Alert>
    );
  }

  const isOpen = operation.type === 'open';
  const actionLabel = isOpen ? 'Apertura cassa' : 'Chiusura cassa';
  const amountLabel = isOpen ? 'Importo apertura' : 'Saldo lasciato in cassa';

  return (
    <Alert severity="info" sx={{ mb: 2 }}>
      <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
        {mode === 'close' ? 'Operazione precedente (apertura)' : 'Ultima operazione (chiusura)'}
      </Typography>
      <Typography variant="body2">
        <strong>{actionLabel}</strong> effettuata da <strong>{getUserLabel(operation.by)}</strong>
      </Typography>
      <Typography variant="body2">
        Data e ora: <strong>{formatDateTime(operation.at)}</strong>
      </Typography>
      {operation.amount != null && (
        <Typography variant="body2" sx={{ mt: 0.5 }}>
          {amountLabel}: <strong>{formatEuro(operation.amount)}</strong>
        </Typography>
      )}
      {!isOpen && mode === 'open' && (
        <Typography variant="body2" sx={{ mt: 1 }}>
          All&apos;apertura di oggi dovresti trovare lo stesso importo in cassa.
        </Typography>
      )}
    </Alert>
  );
}

function CarriedForwardNotesAlert({ notes = [] }) {
  if (!notes.length) return null;

  return (
    <Box sx={{ mb: 2 }}>
      {notes.map((item, index) => (
        <Alert key={`${item.source}-${item.at}-${index}`} severity="warning" sx={{ mb: index < notes.length - 1 ? 1 : 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
            {item.source === 'open'
              ? 'Nota riportata dall\'apertura corrente'
              : 'Nota riportata dalla chiusura precedente'}
          </Typography>
          <Typography variant="body2">
            Registrata da <strong>{getUserLabel(item.by)}</strong> il <strong>{formatDateTime(item.at)}</strong>
          </Typography>
          {item.expectedAmount != null && item.actualAmount != null && (
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              Atteso: <strong>{formatEuro(item.expectedAmount)}</strong> — Rilevato: <strong>{formatEuro(item.actualAmount)}</strong>
            </Typography>
          )}
          <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
            {item.note}
          </Typography>
        </Alert>
      ))}
    </Box>
  );
}

const CashRegisterDialog = ({ open, onClose, onUpdated }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [status, setStatus] = useState(null);
  const [preview, setPreview] = useState(null);
  const [openingAmount, setOpeningAmount] = useState('');
  const [openingDiscrepancyNote, setOpeningDiscrepancyNote] = useState('');
  const [closingCountedAmount, setClosingCountedAmount] = useState('');
  const [closingDiscrepancyNote, setClosingDiscrepancyNote] = useState('');
  const [withdrawnAmount, setWithdrawnAmount] = useState('0');
  const [closeStep, setCloseStep] = useState(0);
  const [euroCounterOpen, setEuroCounterOpen] = useState(false);
  /** @type {'open' | 'close'} */
  const [euroCounterTarget, setEuroCounterTarget] = useState('close');

  const isOpen = !!status?.openSession;
  const expectedOpeningAmount = status?.lastClosedBalance;
  const openingDiffers = amountsDiffer(openingAmount, expectedOpeningAmount);
  const closingDiffers = preview
    ? amountsDiffer(closingCountedAmount, preview.amountInRegister)
    : false;

  const loadData = async () => {
    setLoading(true);
    setError('');
    try {
      const statusRes = await api.get('/cash-register/status');
      setStatus(statusRes.data);

      if (statusRes.data?.openSession) {
        const previewRes = await api.get('/cash-register/close-preview');
        setPreview(previewRes.data);
        setCloseStep(0);
        setWithdrawnAmount('0');
        setClosingCountedAmount(String(previewRes.data?.amountInRegister ?? ''));
        setClosingDiscrepancyNote('');
      } else {
        setPreview(null);
        const suggested = statusRes.data?.lastClosedBalance;
        setOpeningAmount(suggested != null ? String(suggested) : '');
        setOpeningDiscrepancyNote('');
      }
    } catch (err) {
      setError(err.response?.data?.msg || 'Errore nel caricamento dati cassa');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) loadData();
  }, [open]);

  const carriedForwardNotes = isOpen
    ? (preview?.carriedForwardNotes || status?.carriedForwardNotes || [])
    : (status?.carriedForwardNotes || []);

  const countedAmount = parseAmount(closingCountedAmount) || 0;
  const closingBalance = Math.max(0, countedAmount - (parseAmount(withdrawnAmount) || 0));

  const handleOpen = async () => {
    const amount = parseAmount(openingAmount);
    if (!Number.isFinite(amount) || amount < 0) {
      setError('Inserisci un importo di apertura valido');
      return;
    }
    if (openingDiffers && !openingDiscrepancyNote.trim()) {
      setError('L\'importo differisce da quello atteso: inserisci una nota di spiegazione');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await api.post('/cash-register/open', {
        openingAmount: amount,
        openingDiscrepancyNote: openingDiffers ? openingDiscrepancyNote.trim() : undefined
      });
      if (onUpdated) onUpdated();
      onClose();
    } catch (err) {
      setError(err.response?.data?.msg || 'Errore durante l\'apertura cassa');
    } finally {
      setSaving(false);
    }
  };

  const handleCloseStepNext = () => {
    if (closeStep === 0) {
      setError('');
      setCloseStep(1);
      return;
    }
    const counted = parseAmount(closingCountedAmount);
    if (!Number.isFinite(counted) || counted < 0) {
      setError('Inserisci un importo contato valido');
      return;
    }
    if (closingDiffers && !closingDiscrepancyNote.trim()) {
      setError('Il conteggio differisce dal calcolato: inserisci una nota di spiegazione');
      return;
    }
    setError('');
    setCloseStep(2);
  };

  const handleClose = async () => {
    const withdrawn = parseAmount(withdrawnAmount);
    const counted = parseAmount(closingCountedAmount);
    if (!Number.isFinite(withdrawn) || withdrawn < 0) {
      setError('Inserisci un importo prelevato valido');
      return;
    }
    if (!Number.isFinite(counted) || counted < 0) {
      setError('Inserisci un importo contato valido');
      return;
    }
    if (withdrawn > counted) {
      setError('L\'importo prelevato non può superare i contanti contati in cassa');
      return;
    }
    if (closingDiffers && !closingDiscrepancyNote.trim()) {
      setError('Il conteggio differisce dal calcolato: inserisci una nota di spiegazione');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await api.post('/cash-register/close', {
        withdrawnAmount: withdrawn,
        closingCountedAmount: counted,
        closingDiscrepancyNote: closingDiffers ? closingDiscrepancyNote.trim() : undefined
      });
      if (onUpdated) onUpdated();
      onClose();
    } catch (err) {
      setError(err.response?.data?.msg || 'Errore durante la chiusura cassa');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {isOpen ? 'Chiusura Cassa' : 'Apertura Cassa'}
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ pt: 1 }}>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            <CarriedForwardNotesAlert notes={carriedForwardNotes} />

            {!isOpen && (
              <>
                <PreviousOperationAlert
                  operation={status?.previousOperation}
                  mode="open"
                />
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Operatore attuale: <strong>{user?.name || user?.email}</strong>
                </Typography>
                {expectedOpeningAmount != null && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Importo atteso in cassa: <strong>{formatEuro(expectedOpeningAmount)}</strong>
                  </Alert>
                )}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'flex-start', mb: 2 }}>
                  <TextField
                    label="Contanti presenti in cassa"
                    type="number"
                    fullWidth
                    value={openingAmount}
                    onChange={(e) => setOpeningAmount(e.target.value)}
                    inputProps={{ min: 0, step: '0.01' }}
                    helperText="Importo fisico contato all'apertura"
                    sx={{ flex: '1 1 220px' }}
                  />
                  <Button
                    variant="outlined"
                    onClick={() => {
                      setEuroCounterTarget('open');
                      setEuroCounterOpen(true);
                    }}
                    sx={{
                      flex: '0 0 auto',
                      minHeight: 56,
                      px: 2,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 0.25
                    }}
                  >
                    <BanknoteButtonArt size={36} />
                    <Typography variant="button" sx={{ fontSize: '0.75rem', lineHeight: 1.2 }}>
                      Calcola
                    </Typography>
                  </Button>
                </Box>
                {openingDiffers && (
                  <>
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      Lo scostamento è di <strong>{formatEuro(parseAmount(openingAmount) - parseAmount(expectedOpeningAmount))}</strong>.
                      È obbligatorio inserire una nota che verrà riportata alla prossima chiusura.
                    </Alert>
                    <TextField
                      label="Nota di spiegazione scostamento apertura"
                      fullWidth
                      multiline
                      minRows={3}
                      required
                      value={openingDiscrepancyNote}
                      onChange={(e) => setOpeningDiscrepancyNote(e.target.value)}
                      helperText="Questa nota sarà visibile alla chiusura cassa di oggi"
                    />
                  </>
                )}
              </>
            )}

            {isOpen && preview && (
              <>
                <PreviousOperationAlert
                  operation={status?.previousOperation}
                  mode="close"
                />

                <Stepper activeStep={closeStep} sx={{ mb: 3 }}>
                  <Step><StepLabel>Recap</StepLabel></Step>
                  <Step><StepLabel>Conteggio fisico</StepLabel></Step>
                  <Step><StepLabel>Prelievo finale</StepLabel></Step>
                </Stepper>

                {closeStep === 0 && (
                  <Box>
                    <Box sx={{ bgcolor: 'grey.50', borderRadius: 1, px: 2, py: 1, mb: 2 }}>
                      <RecapRow label="Contanti ad apertura" value={formatEuro(preview.openingAmount)} />
                      <RecapRow label="Incassi in contanti" value={formatEuro(preview.cashInTotal)} />
                      <RecapRow label="Uscite in contanti" value={formatEuro(preview.cashOutTotal)} />
                      <RecapRow label="Prelievi (cassa → cassaforte)" value={formatEuro(preview.transferPrelievoTotal)} />
                      <RecapRow label="Versamenti (cassaforte → cassa)" value={formatEuro(preview.transferVersamentoTotal)} />
                      <Divider sx={{ my: 1 }} />
                      <RecapRow label="Saldo calcolato in cassa" value={formatEuro(preview.amountInRegister)} highlight />
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                        Solo movimenti contanti con data valuta del {preview.valueDate},
                        registrati dall&apos;apertura di questa sessione ({formatDateTime(preview.openedAt)}).
                      </Typography>
                    </Box>
                    {preview.transfers?.length > 0 && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>Dettaglio prelievi/versamenti</Typography>
                        {preview.transfers.map((transfer) => (
                          <Typography key={transfer.id} variant="body2" sx={{ mb: 0.5 }}>
                            {transfer.type === 'PRELIEVO' ? 'Prelievo' : 'Versamento'}: {formatEuro(transfer.amount)}
                            {' '}— {formatDateTime(transfer.createdAt)}
                            {transfer.note ? ` (${transfer.note})` : ''}
                          </Typography>
                        ))}
                      </Box>
                    )}
                  </Box>
                )}

                {closeStep === 1 && (
                  <Box>
                    <Alert severity="info" sx={{ mb: 2 }}>
                      Saldo calcolato: <strong>{formatEuro(preview.amountInRegister)}</strong>. Conta i contanti fisici presenti nel cassetto.
                    </Alert>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'flex-start', mb: 2 }}>
                      <TextField
                        label="Contanti fisici contati in cassa"
                        type="number"
                        fullWidth
                        value={closingCountedAmount}
                        onChange={(e) => setClosingCountedAmount(e.target.value)}
                        inputProps={{ min: 0, step: '0.01' }}
                        helperText="Conteggio fisico effettuato alla chiusura"
                        sx={{ flex: '1 1 220px' }}
                      />
                      <Button
                        variant="outlined"
                        onClick={() => {
                          setEuroCounterTarget('close');
                          setEuroCounterOpen(true);
                        }}
                        sx={{
                          flex: '0 0 auto',
                          minHeight: 56,
                          px: 2,
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: 0.25
                        }}
                      >
                        <BanknoteButtonArt size={36} />
                        <Typography variant="button" sx={{ fontSize: '0.75rem', lineHeight: 1.2 }}>
                          Calcola
                        </Typography>
                      </Button>
                    </Box>
                    {closingDiffers && (
                      <>
                        <Alert severity="warning" sx={{ mb: 2 }}>
                          Lo scostamento rispetto al calcolato è di{' '}
                          <strong>{formatEuro(parseAmount(closingCountedAmount) - parseAmount(preview.amountInRegister))}</strong>.
                          È obbligatorio inserire una nota che verrà riportata alla prossima apertura.
                        </Alert>
                        <TextField
                          label="Nota di spiegazione scostamento chiusura"
                          fullWidth
                          multiline
                          minRows={3}
                          required
                          value={closingDiscrepancyNote}
                          onChange={(e) => setClosingDiscrepancyNote(e.target.value)}
                          helperText="Questa nota sarà visibile alla prossima apertura cassa"
                        />
                      </>
                    )}
                  </Box>
                )}

                {closeStep === 2 && (
                  <Box>
                    <TextField
                      label="Importo prelevato (cassaforte)"
                      type="number"
                      fullWidth
                      value={withdrawnAmount}
                      onChange={(e) => setWithdrawnAmount(e.target.value)}
                      inputProps={{ min: 0, step: '0.01' }}
                      helperText="Contanti ritirati dalla cassa e depositati in cassaforte (può essere 0)"
                      sx={{ mb: 2 }}
                    />
                    <Box sx={{ bgcolor: 'grey.50', borderRadius: 1, px: 2, py: 1, mb: 2 }}>
                      <RecapRow label="Contanti contati" value={formatEuro(closingCountedAmount)} />
                      <RecapRow label="Prelievi in giornata" value={formatEuro(preview.transferPrelievoTotal)} />
                      <RecapRow label="Prelievo chiusura (cassaforte)" value={formatEuro(withdrawnAmount)} />
                      <Divider sx={{ my: 1 }} />
                      <RecapRow
                        label="Totale prelievi"
                        value={formatEuro((parseAmount(preview.transferPrelievoTotal) || 0) + (parseAmount(withdrawnAmount) || 0))}
                      />
                      <RecapRow label="Saldo lasciato in cassa" value={formatEuro(closingBalance)} highlight />
                    </Box>
                    <Alert severity="info" sx={{ mt: 2 }}>
                      Il saldo in cassa ({formatEuro(closingBalance)}) sarà l&apos;importo atteso alla prossima apertura.
                    </Alert>
                  </Box>
                )}
              </>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Annulla</Button>
        {!loading && !isOpen && (
          <Button variant="contained" color="success" onClick={handleOpen} disabled={saving}>
            {saving ? 'Salvataggio...' : 'Conferma apertura'}
          </Button>
        )}
        {!loading && isOpen && closeStep === 0 && (
          <Button variant="contained" onClick={handleCloseStepNext}>
            Avanti
          </Button>
        )}
        {!loading && isOpen && closeStep === 1 && (
          <>
            <Button onClick={() => setCloseStep(0)} disabled={saving}>Indietro</Button>
            <Button variant="contained" onClick={handleCloseStepNext}>
              Avanti
            </Button>
          </>
        )}
        {!loading && isOpen && closeStep === 2 && (
          <>
            <Button onClick={() => setCloseStep(1)} disabled={saving}>Indietro</Button>
            <Button variant="contained" color="warning" onClick={handleClose} disabled={saving}>
              {saving ? 'Chiusura...' : 'Conferma chiusura'}
            </Button>
          </>
        )}
      </DialogActions>

      <EuroCashCounterDialog
        open={euroCounterOpen}
        onClose={() => setEuroCounterOpen(false)}
        onApply={(total) => {
          const formatted = total.toFixed(2);
          if (euroCounterTarget === 'open') {
            setOpeningAmount(formatted);
          } else {
            setClosingCountedAmount(formatted);
          }
          setError('');
        }}
        title={
          euroCounterTarget === 'open'
            ? 'Conteggio banconote e monete € — apertura'
            : 'Conteggio banconote e monete € — chiusura'
        }
        applyHint={
          euroCounterTarget === 'open'
            ? 'Il totale verrà copiato nel campo contanti di apertura cassa.'
            : 'Il totale verrà copiato nel campo contanti di chiusura cassa.'
        }
      />
    </Dialog>
  );
};

export default CashRegisterDialog;
